import { LitElement, html, css, nothing } from 'lit';
import { PRINT_STYLES } from './print-styles.js';
import { repeat } from 'lit/directives/repeat.js';
import { StoreMixin, store } from '../store.js';
import { api } from '../api.js';
import { navigate, setFilters } from '../router.js';
import { apiParams } from '../lib/filters.js';
import {
  BENCH_DIMENSIONS,
  BENCH_PARAM_KEYS,
  CALLOUTS_SHOWN,
  benchExportRows,
  benchScaleMax,
  benchStack,
  benchSummary,
  calloutText,
  foldRoles,
  pipelineLineLabel,
  roleExportRows,
  benchCalloutColumns,
  benchSegments,
  benchRoleColumns,
  benchWeekColumns,
} from '../lib/bench.js';
import { withFormats } from '../lib/table.js';
import { hours as formatHours, percent, weekLabel, longDate, count } from '../lib/format.js';

/**
 * The bench (V-16, Phase 29; PRD 10.7): unallocated hours by role, stacked per week, against
 * the pipeline line that could fill them. The inverse of the demand ribbon, and its toggle on
 * the dashboard: where the ribbon asks when demand crosses capacity, the bench asks how much
 * capacity is going spare, in which roles, and whether anything in the pipeline is aimed at it.
 * Where the bars stand above the line, the difference is the sales target, and the callouts
 * beneath name the role and the weeks.
 *
 * Everything drawn here comes from `GET /forecast/bench`; the component adds no arithmetic of
 * its own beyond stacking the role values it was given and folding the roles past the seventh
 * into one neutral segment (`lib/bench.js`), so what the tooltip shows is what the API said and
 * what the table exports.
 *
 * Roles are drawn as keyed groups (`repeat` on the role key), so a filter change updates the
 * geometry of the surviving roles in place rather than repainting them (PRD 7.3).
 *
 * The marks are rendered by `chart-frame` inside *its* shadow root, so no stylesheet of this
 * component reaches them: every mark carries its presentation as attributes, with tokens as the
 * values.
 */
class BenchForecast extends StoreMixin(LitElement) {
  static properties = {
    _data: { state: true },
    _loading: { state: true },
    _error: { state: true },
    _hover: { state: true },
    _tableOpen: { state: true },
    _rolesOpen: { state: true },
    _calloutsOpen: { state: true },
  };

  constructor() {
    super();
    this._data = null;
    this._loading = false;
    this._error = null;
    this._hover = null;
    this._tableOpen = true;
    this._rolesOpen = false;
    this._calloutsOpen = false;
    this._requestKey = null;
    this._fold = null;
    this._foldFor = null;
  }

  static styles = [
    PRINT_STYLES,
    css`
      :host {
        display: block;
      }
      .card {
        background: var(--surface);
        border: 1px solid var(--border-soft);
        border-radius: var(--radius-card);
        box-shadow: var(--shadow-card);
        padding: 20px 24px 18px;
      }
      .head {
        display: flex;
        flex-wrap: wrap;
        align-items: baseline;
        gap: 6px 18px;
        margin-bottom: 12px;
      }
      .eyebrow {
        font-family: var(--font-eyebrow);
        font-size: 10px;
        font-weight: 700;
        letter-spacing: 0.16em;
        text-transform: uppercase;
        color: var(--ink-muted);
      }
      h2 {
        margin: 2px 0 0;
        font-size: 20px;
        line-height: 1.2;
        color: var(--ink-strong);
      }
      .sub {
        color: var(--ink-muted);
        font-size: 12.5px;
      }
      .kpis {
        margin-left: auto;
        display: flex;
        gap: 18px;
        flex-wrap: wrap;
      }
      .kpi {
        text-align: right;
      }
      .kpi .n {
        font-size: 18px;
        font-weight: 700;
        color: var(--ink-strong);
        font-variant-numeric: tabular-nums;
        line-height: 1.1;
      }
      .kpi .l {
        font-size: 11px;
        color: var(--ink-muted);
      }
      .legend-row {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: 8px 22px;
        margin-top: 10px;
      }
      .line-key {
        display: inline-flex;
        align-items: center;
        gap: 7px;
        font-size: 12.5px;
        color: var(--ink);
      }
      .line-key svg {
        display: block;
      }
      /* The pipeline line is a total; the funnel (Phase 33) is what it is made of. */
      button.to-funnel {
        font: inherit;
        font-size: 12.5px;
        font-weight: 600;
        color: var(--link);
        background: none;
        border: 0;
        padding: 0;
        cursor: pointer;
      }
      button.to-funnel:hover {
        text-decoration: underline;
      }
      .callouts {
        margin: 14px 0 0;
        padding: 0;
        list-style: none;
        display: grid;
        gap: 6px;
      }
      .callouts li {
        display: flex;
        flex-wrap: wrap;
        align-items: baseline;
        gap: 4px 10px;
        font-size: 13px;
        color: var(--ink);
      }
      .callouts .gap {
        font-weight: 700;
        font-variant-numeric: tabular-nums;
        color: var(--ink-strong);
        min-width: 5.5em;
        text-align: right;
      }
      .callouts button {
        font: inherit;
        font-weight: 600;
        color: var(--link);
        background: none;
        border: 0;
        padding: 0;
        cursor: pointer;
        text-align: left;
      }
      .callouts button:hover {
        text-decoration: underline;
      }
      .callouts .when {
        color: var(--ink-muted);
        font-size: 12.5px;
      }
      .callouts-title {
        margin: 16px 0 0;
        font-family: var(--font-eyebrow);
        font-size: 10px;
        font-weight: 700;
        letter-spacing: 0.16em;
        text-transform: uppercase;
        color: var(--ink-muted);
      }
      .note {
        margin: 10px 0 0;
        font-size: 12.5px;
        color: var(--ink-muted);
      }
      .state {
        padding: 36px 0;
        text-align: center;
        color: var(--ink-muted);
      }
      .state.error {
        color: var(--status-overloaded);
        font-weight: 600;
      }
      details.table {
        margin-top: 16px;
        border-top: 1px solid var(--border-soft);
        padding-top: 12px;
      }
      details.table summary {
        cursor: pointer;
        font-size: 13px;
        font-weight: 600;
        color: var(--link);
        list-style: none;
      }
      details.table summary::-webkit-details-marker {
        display: none;
      }
      details.table summary::before {
        content: '▸ ';
        color: var(--ink-muted);
      }
      details.table[open] summary::before {
        content: '▾ ';
      }
      details.table data-table {
        margin-top: 10px;
      }
    `,
  ];

  connectedCallback() {
    super.connectedCallback();
    // Fetch off the store, not off the render cycle: a filter or run change asks the API only
    // when a parameter this view reads has moved.
    this._unsubscribeFetch = store.subscribe(() => this._maybeFetch());
    this._maybeFetch();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this._unsubscribeFetch?.();
  }

  /** The request this view wants for the current filters, or null when there is no run. */
  _params() {
    const { filters, run } = this.appState;
    if (!run) return null;
    const params = apiParams(filters, BENCH_PARAM_KEYS);
    if (!BENCH_DIMENSIONS.includes(params.dimension)) {
      delete params.dimension;
      delete params.entity;
    }
    return params;
  }

  async _maybeFetch() {
    const params = this._params();
    const key = params ? JSON.stringify(params) : null;
    if (key === this._requestKey) return;
    this._requestKey = key;
    if (!params) {
      this._data = null;
      return;
    }
    this._loading = true;
    this._error = null;
    try {
      const data = await api.get('/forecast/bench', params);
      if (this._requestKey !== key) return; // a newer request has taken over
      this._data = data;
    } catch (err) {
      if (this._requestKey !== key) return;
      this._error = err.message;
      this._data = null;
    } finally {
      if (this._requestKey === key) this._loading = false;
    }
  }

  // -------------------------------------------------------------------------------------------
  // Model

  get _weeks() {
    return this._data?.weeks ?? [];
  }

  /** The hued roles and the folded rest, computed once per response. */
  get _foldRoles() {
    if (this._foldFor !== this._data) {
      this._fold = foldRoles(this._data?.roles ?? []);
      this._foldFor = this._data;
    }
    return this._fold;
  }

  /** The segments in drawing order, with the folded segment last (shared with the CSV). */
  get _segments() {
    return benchSegments(this._foldRoles);
  }

  _stackOf(week) {
    return benchStack(week.roles, this._foldRoles);
  }

  // -------------------------------------------------------------------------------------------
  // Drawing

  _pipelinePath(x, y) {
    const weeks = this._weeks;
    if (!weeks.length) return '';
    // The pipeline is a weekly figure, so the line steps: flat across each band, joined at the
    // edges, the way the ribbon steps its capacity line.
    const pad = (x.step() - x.bandwidth()) / 2;
    const parts = weeks.map((w) => {
      const x0 = x(w.week_start) - pad;
      const x1 = x0 + x.step();
      return `${x0},${y(w.pipeline_hours)} L${x1},${y(w.pipeline_hours)}`;
    });
    return `M${parts.join(' L')}`;
  }

  _marks = ({ x, y, plot, svg: s }) => {
    const weeks = this._weeks;
    const segments = this._segments;
    const stacks = new Map(weeks.map((w) => [w.week_start, this._stackOf(w)]));
    const last = weeks.at(-1);
    const lineLabel = pipelineLineLabel(this._data?.pipeline_mode);
    return s`
      <g class="roles">
        ${repeat(
          segments,
          (seg) => seg.key,
          (seg, i) => s`
            <g class="role" data-role=${seg.key}>
              ${weeks.map((w) => {
                const part = stacks.get(w.week_start)[i];
                if (!part || part.hours <= 0) return nothing;
                return s`<rect class="seg" data-week=${w.week_start}
                              x=${x(w.week_start)} width=${x.bandwidth()}
                              y=${y(part.to)} height=${Math.max(0, y(part.from) - y(part.to))}
                              fill=${`var(${seg.token})`} stroke="var(--surface)" stroke-width="1" />`;
              })}
            </g>`,
        )}
      </g>
      <g class="gaps">
        ${weeks.map((w) => {
          if (w.gap_hours <= 0) return nothing;
          const cx = x(w.week_start) + x.bandwidth() + 2;
          const y0 = y(w.pipeline_hours);
          const y1 = y(w.bench_hours);
          return s`<line class="gap" x1=${cx} x2=${cx} y1=${y0} y2=${y1}
                         stroke="var(--ink-strong)" stroke-width="1" />
                   <line x1=${cx - 2} x2=${cx + 2} y1=${y1} y2=${y1} stroke="var(--ink-strong)" stroke-width="1" />
                   <line x1=${cx - 2} x2=${cx + 2} y1=${y0} y2=${y0} stroke="var(--ink-strong)" stroke-width="1" />
                   <text class="gap-label" x=${x.centre(w.week_start)} y=${y1 - 4} text-anchor="middle"
                         fill="var(--ink-muted)" font-size="9.5" font-family="var(--font-sans)"
                         font-variant-numeric="tabular-nums">${formatHours(w.gap_hours, { unit: false })}</text>`;
        })}
      </g>
      <path class="pipeline" d=${this._pipelinePath(x, y)} fill="none"
            stroke="var(--ink-strong)" stroke-width="2" stroke-dasharray="5 3" stroke-linejoin="round" />
      ${
        last
          ? s`<text class="pipeline-label" x=${x(last.week_start) + x.bandwidth()}
                    y=${y(last.pipeline_hours) - 6} text-anchor="end"
                    fill="var(--ink-strong)" font-size="11" font-weight="600"
                    font-family="var(--font-sans)">${lineLabel}</text>`
          : nothing
      }
      ${
        this._hover !== null && weeks[this._hover]
          ? s`<line class="crosshair" x1=${x.centre(weeks[this._hover].week_start)}
                    x2=${x.centre(weeks[this._hover].week_start)} y1=${plot.top} y2=${plot.top + plot.height}
                    stroke="var(--ink-muted)" stroke-width="1" stroke-dasharray="2 2" pointer-events="none" />`
          : nothing
      }
      <g class="hits">
        ${weeks.map(
          (w, i) => s`
            <rect class="hit" x=${x(w.week_start) - (x.step() - x.bandwidth()) / 2} width=${x.step()}
                  y=${plot.top} height=${plot.height} fill="transparent" style="cursor: crosshair"
                  @pointermove=${(e) => this._showTooltip(e, i)}
                  @pointerleave=${() => this._hideTooltip()}
                  @focus=${(e) => this._showTooltip(e, i)}
                  @blur=${() => this._hideTooltip()}
                  tabindex="0" role="img"
                  aria-label=${`Week of ${weekLabel(w.week_start)}: ${formatHours(w.bench_hours)} on the bench against ${formatHours(w.pipeline_hours)} of pipeline`} />`,
        )}
      </g>
    `;
  };

  // -------------------------------------------------------------------------------------------
  // Hover

  _showTooltip(event, index) {
    const w = this._weeks[index];
    if (!w) return;
    this._hover = index;
    const tooltip = this.renderRoot.querySelector('chart-tooltip');
    if (!tooltip) return;
    const rect = event.currentTarget?.getBoundingClientRect?.();
    const rows = [...this._stackOf(w)].reverse().map((seg) => ({
      label: seg.roles ? `${seg.label} (${count(seg.roles.length)})` : seg.label,
      value: formatHours(seg.hours, { exact: true }),
      token: seg.token,
      fill: 'solid',
    }));
    rows.push({ label: 'On the bench', value: formatHours(w.bench_hours, { exact: true }) });
    rows.push({
      label: pipelineLineLabel(this._data?.pipeline_mode),
      value: formatHours(w.pipeline_hours, { exact: true }),
    });
    rows.push(
      w.gap_hours > 0
        ? {
            label: 'Gap (bench the pipeline does not reach)',
            value: formatHours(w.gap_hours, { exact: true }),
          }
        : {
            label: 'Pipeline over the bench',
            value: formatHours(w.surplus_hours, { exact: true }),
          },
    );
    const footer = [
      `${formatHours(w.capacity_hours)} capacity · ${formatHours(w.committed_hours)} committed`,
      w.role_gap_hours > w.gap_hours
        ? `${formatHours(w.role_gap_hours)} gap matched by role`
        : null,
      `${count(w.roles_with_bench)} roles with bench · ${count(w.people)} people`,
    ]
      .filter(Boolean)
      .join(' · ');
    tooltip.show({
      x: event.clientX ?? (rect ? rect.left + rect.width / 2 : 0),
      y: event.clientY ?? (rect ? rect.top : 0),
      title: `Week of ${longDate(w.week_start)}`,
      rows,
      footer,
    });
  }

  _hideTooltip() {
    this._hover = null;
    this.renderRoot.querySelector('chart-tooltip')?.hide();
  }

  // -------------------------------------------------------------------------------------------
  // Callouts

  _narrowTo(role) {
    setFilters({ dimension: 'service', entity: role });
  }

  _callouts() {
    const all = this._data?.callouts ?? [];
    if (!all.length)
      return html`<p class="note">
        No gap: the pipeline reaches every role's bench in every week of the window.
      </p>`;
    const shown = all.slice(0, CALLOUTS_SHOWN);
    return html`
      <div class="callouts-title">Where the pipeline falls short</div>
      <ul class="callouts">
        ${repeat(
          shown,
          (c) => `${c.role}|${c.from}`,
          (c) => html`
            <li data-role=${c.role}>
              <span class="gap">${formatHours(c.gap_hours)}</span>
              <button
                type="button"
                @click=${() => this._narrowTo(c.role)}
                title=${`Narrow the charts to ${c.label}`}
              >
                ${c.label}
              </button>
              <span class="when">
                ${c.weeks > 1 ? `${count(c.weeks)} weeks from ${weekLabel(c.from)}` : `week of ${weekLabel(c.from)}`}
                ·
                ${
                  c.pipeline_hours > 0
                    ? `${formatHours(c.pipeline_hours)} of pipeline against ${formatHours(c.bench_hours)} on the bench`
                    : `${formatHours(c.bench_hours)} on the bench, no pipeline against it`
                }
              </span>
            </li>
          `,
        )}
      </ul>
      ${
        all.length > shown.length
          ? html`<p class="note">${count(all.length - shown.length)} more in the table below.</p>`
          : nothing
      }
    `;
  }

  // -------------------------------------------------------------------------------------------
  // Tables

  get _weekColumns() {
    // The specification is shared with the server's CSV (lib/bench.js); only the drawing is here.
    return withFormats(
      benchWeekColumns({ segments: this._segments, pipelineMode: this._data?.pipeline_mode }),
      {
        byUnit: {
          hours: (v) => formatHours(v, { exact: true }),
          percent: (v) => percent(v),
          week: (v) => weekLabel(v),
        },
      },
    );
  }

  get _roleColumns() {
    return withFormats(benchRoleColumns({ pipelineMode: this._data?.pipeline_mode }), {
      byUnit: {
        hours: (v) => formatHours(v, { exact: true }),
        percent: (v) => percent(v),
        week: (v) => weekLabel(v),
      },
    });
  }

  get _calloutColumns() {
    return withFormats(benchCalloutColumns({ pipelineMode: this._data?.pipeline_mode }), {
      byUnit: {
        hours: (v) => formatHours(v, { exact: true }),
        percent: (v) => percent(v),
        week: (v) => weekLabel(v),
      },
    });
  }

  // -------------------------------------------------------------------------------------------
  // Render

  _subtitle() {
    const d = this._data;
    const { filters } = this.appState;
    if (!d) return '';
    const who = d.entity ? `${d.entity.label} (${d.dimension})` : 'Whole company';
    const notes = [];
    if (!BENCH_DIMENSIONS.includes(filters.dimension))
      notes.push('the bench has no project dimension, so the company is shown');
    if (filters.grain !== 'week') notes.push('weekly grain (the bench is weekly)');
    return `${who} · ${d.weeks_count} weeks from ${weekLabel(d.from)} · pipeline ${d.pipeline_mode}${
      notes.length ? ` · ${notes.join('; ')}` : ''
    }`;
  }

  _kpis() {
    if (!this._data) return nothing;
    const k = benchSummary(this._data);
    return html`
      <div class="kpis">
        <div class="kpi" data-kpi="bench">
          <div class="n">${formatHours(k.bench_hours)}</div>
          <div class="l">
            on the bench in ${count(k.weeks_with_bench)} of ${count(k.weeks)} weeks
          </div>
        </div>
        <div class="kpi" data-kpi="pipeline">
          <div class="n">${k.coverage === null ? '—' : percent(k.coverage)}</div>
          <div class="l">
            covered by ${formatHours(k.pipeline_hours)} of ${this._data.pipeline_mode} pipeline
          </div>
        </div>
        <div class="kpi" data-kpi="gap">
          <div class="n">${formatHours(k.gap_hours)}</div>
          <div class="l">
            gap in ${count(k.weeks_with_gap)} of ${count(k.weeks)}
            weeks${k.peak ? `, most on ${weekLabel(k.peak.week_start)}` : ''}
          </div>
        </div>
        <div class="kpi" data-kpi="roles">
          <div class="n">${count(k.roles_with_bench)}</div>
          <div class="l">roles with bench</div>
        </div>
      </div>
    `;
  }

  _legend() {
    const segments = [...this._segments].reverse(); // top of the stack first, as the eye reads it
    return html`
      <div class="legend-row">
        <chart-legend
          .items=${segments.map((seg) => ({
            label: seg.roles ? `${seg.label} (${count(seg.roles.length)})` : seg.label,
            token: seg.token,
            fill: 'solid',
          }))}
        ></chart-legend>
        <span class="line-key">
          <svg width="26" height="8" aria-hidden="true">
            <line
              x1="0"
              x2="26"
              y1="4"
              y2="4"
              stroke="var(--ink-strong)"
              stroke-width="2"
              stroke-dasharray="5 3"
            />
          </svg>
          <span>${pipelineLineLabel(this._data?.pipeline_mode)}</span>
        </span>
        <span class="line-key">
          <svg width="10" height="14" aria-hidden="true">
            <line x1="5" x2="5" y1="1" y2="13" stroke="var(--ink-strong)" stroke-width="1" />
            <line x1="3" x2="7" y1="1" y2="1" stroke="var(--ink-strong)" stroke-width="1" />
            <line x1="3" x2="7" y1="13" y2="13" stroke="var(--ink-strong)" stroke-width="1" />
          </svg>
          <span>Gap: bench the pipeline does not reach</span>
        </span>
        <button class="to-funnel" type="button" @click=${() => navigate('/funnel')}>
          What is in that line? →
        </button>
      </div>
    `;
  }

  render() {
    const { run } = this.appState;
    const d = this._data;
    const weeks = this._weeks;

    let body;
    if (!run) body = html`<div class="state">No forecast run yet — nothing to draw.</div>`;
    else if (this._error) body = html`<div class="state error">${this._error}</div>`;
    else if (!d && this._loading) body = html`<div class="state">Loading the bench…</div>`;
    else if (!d || weeks.length === 0)
      body = html`<div class="state">No weeks in this window.</div>`;
    else
      body = html`
        <chart-frame
          .categories=${weeks.map((w) => w.week_start)}
          .max=${benchScaleMax(weeks)}
          .marks=${this._marks}
          .formatCategory=${(c) => weekLabel(c)}
          value-label="Hours"
          category-label="Week commencing"
          height="320"
        ></chart-frame>
        ${this._legend()}
        ${
          d.totals.roles_with_bench === 0
            ? html`<p class="note" data-note="no-bench">
                Nothing on the bench: every role is at or over capacity in every week of the window.
              </p>`
            : nothing
        }
        ${
          d.pipeline_mode === 'none'
            ? html`<p class="note" data-note="pipeline-excluded">
                Opportunities are excluded, so the line sits at zero and every bench hour is a gap.
              </p>`
            : nothing
        }
        ${this._callouts()}
        <details
          class="table"
          ?open=${this._tableOpen}
          @toggle=${(e) => (this._tableOpen = e.target.open)}
        >
          <summary>Table view: weeks</summary>
          <data-table
            caption=${`Unallocated hours by role against the pipeline, per week — ${this._subtitle()}`}
            export-name="bench-forecast"
            page-size="13"
            dense
            .columns=${this._weekColumns}
            .rows=${benchExportRows(d, this._foldRoles)}
          ></data-table>
        </details>
        <details
          class="table"
          ?open=${this._rolesOpen}
          @toggle=${(e) => (this._rolesOpen = e.target.open)}
        >
          <summary>Table view: roles</summary>
          <data-table
            caption=${`Roles over the window: capacity, committed, bench, pipeline and gap — ${this._subtitle()}`}
            export-name="bench-roles"
            page-size="25"
            dense
            .columns=${this._roleColumns}
            .rows=${roleExportRows(d)}
          ></data-table>
        </details>
        <details
          class="table"
          ?open=${this._calloutsOpen}
          @toggle=${(e) => (this._calloutsOpen = e.target.open)}
        >
          <summary>Table view: gaps by role</summary>
          <data-table
            caption=${`Every run of weeks where a role's bench exceeds its pipeline — ${this._subtitle()}`}
            export-name="bench-callouts"
            page-size="25"
            dense
            .columns=${this._calloutColumns}
            .rows=${d.callouts.map((c) => ({ ...c, text: calloutText(c) }))}
          ></data-table>
        </details>
      `;

    return html`
      <div class="card" aria-busy=${this._loading ? 'true' : 'false'}>
        <div class="head">
          <div>
            <div class="eyebrow">Bench</div>
            <h2>How much capacity is going spare, in which roles, and is anything aimed at it?</h2>
            <div class="sub">${this._subtitle()}</div>
          </div>
          ${this._kpis()}
        </div>
        ${body}
        <chart-tooltip></chart-tooltip>
      </div>
    `;
  }
}

customElements.define('bench-forecast', BenchForecast);
